import React from 'react';

import { Group, Select, Stack, Text } from '@mantine/core';
import type { PlotlyTypes } from 'visyn_core/plotly';
import { PlotlyComponent } from 'visyn_core/plotly';

import { CLONE_SIZE_CATEGORY_ORDER, getShmViolinTracesFromSeries } from './repertoire-analysis-utils';
import { CLONOTYPE_SIZE_OPTION, IG_ISOTYPE_OPTION, VH_FAMILY_OPTION } from '../constants/plot-options';
import type { BinnedData } from '../generated/generatedOpenAPI';

interface DistributionOfSHMViolinPlotProps {
  shmViolinData: BinnedData | null | undefined;
}

export default function DistributionOfSHMViolinPlot({ shmViolinData }: DistributionOfSHMViolinPlotProps) {
  const hasIgIsotypeData = Boolean(shmViolinData?.igIsotype?.length);
  const categorySelectorData = React.useMemo(() => {
    const data = [CLONOTYPE_SIZE_OPTION, VH_FAMILY_OPTION];
    if (hasIgIsotypeData) {
      data.push(IG_ISOTYPE_OPTION);
    }
    return data;
  }, [hasIgIsotypeData]);

  const [userShmDistributionCategoryOption, setUserShmDistributionCategoryOption] = React.useState<string | null>(null);
  const shmDistributionCategoryOption =
    userShmDistributionCategoryOption ?? (categorySelectorData.includes(IG_ISOTYPE_OPTION) ? IG_ISOTYPE_OPTION.value : CLONOTYPE_SIZE_OPTION.value);

  const { data, categoryValues } = React.useMemo(() => {
    if (shmDistributionCategoryOption === IG_ISOTYPE_OPTION.value) {
      return getShmViolinTracesFromSeries(shmViolinData?.igIsotype ?? []);
    }
    if (shmDistributionCategoryOption === VH_FAMILY_OPTION.value) {
      return getShmViolinTracesFromSeries(shmViolinData?.vhFamily ?? []);
    }
    if (shmDistributionCategoryOption === CLONOTYPE_SIZE_OPTION.value) {
      return getShmViolinTracesFromSeries(shmViolinData?.cloneSizeCategory ?? [], CLONE_SIZE_CATEGORY_ORDER);
    }
    return getShmViolinTracesFromSeries([]);
  }, [shmDistributionCategoryOption, shmViolinData]);

  return (
    <Stack gap="md">
      <Text size="xl">Stratified distribution of SHM in Heavy and Light Chains</Text>
      <Group gap={7}>
        <Text>Distribution type:</Text>
        <Select
          data-testid="shm-distribution-violin-option-select"
          value={shmDistributionCategoryOption}
          onChange={(value) => {
            if (typeof value === 'string') {
              setUserShmDistributionCategoryOption(value);
            }
          }}
          data={categorySelectorData}
          w={150}
        />
      </Group>
      <div data-testid="shm-distribution-violin-plot">
        <PlotlyComponent
          data={data as PlotlyTypes.Data[]}
          style={{ width: '100%' }}
          layout={{
            hovermode: false,
            xaxis: {
              type: 'category',
              autorange: false,
              range: [-0.5, categoryValues.length - 0.5],
              categoryarray: categoryValues,
              categoryorder: 'array',
            },
            yaxis: {
              zeroline: false,
              title: {
                text: 'Number of AA differences<br>from germline',
                font: { size: 16 },
              },
              layer: 'above traces',
            },
            grid: { xgap: 0.2, ygap: 0.2 },
            height: 320,
            margin: {
              l: 60,
              r: 10,
              b: 50,
              t: 20,
            },
            legend: {
              font: {
                family: 'Roboto, sans-serif',
                size: 14,
              },
            },
          }}
          config={{
            displaylogo: false,
            modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
          }}
        />
      </div>
    </Stack>
  );
}
